import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import type { WbBoardScene } from "../../lib/board/wb-board-scene";
import {
  canControlPdfDocument,
  isPdfBroadcastActive,
  isPdfDocumentObject,
  pdfCurrentPageAssetId,
} from "../../editor/document/documentContext";
import { downloadPresentationPdf } from "../../lib/pdf/downloadPresentationPdf";
import { createLazyThumbStrip, type LazyThumbStrip } from "../../lib/board/lazyThumbStrip";

/** Узкий экран: телефон. От этого зависит раскладка панели. */
function useNarrowScreen(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 720px)");
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    onChange();
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return narrow;
}

type Props = {
  objectId: string;
  scene: InstanceType<typeof WbBoardScene>;
  sceneTick: number;
  canEdit: boolean;
  onCloseLocal: () => void;
  onDownloaded?: () => void;
  onError?: (message: string) => void;
  /** Для режима «Доска»: pan/zoom основной области доски с основного слайда. */
  viewportRef?: RefObject<HTMLDivElement | null>;
};

/** Докуда приближается слайд щипком. Дальше текст уже крупнее экрана. */
const MAX_SLIDE_ZOOM = 4;

export function PresentationOverlay({
  objectId,
  scene,
  sceneTick,
  canEdit,
  onCloseLocal,
  onDownloaded,
  onError,
  viewportRef,
}: Props) {
  void sceneTick;
  const thumbsRef = useRef<HTMLDivElement>(null);
  const thumbsScrollRef = useRef({ top: 0, left: 0 });
  const panelRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<LazyThumbStrip | null>(null);
  /**
   * Приближение слайда щипком.
   *
   * На телефоне слайд занимает ладонь, и мелкий текст с формулами не
   * прочитать. Своё приближение, а не страничное: страничное увело бы
   * вместе со слайдом и панель с кнопками, а у ведущего ещё и сбивало бы
   * показ. Видно это только тому, кто приближает.
   */
  const [slideZoom, setSlideZoom] = useState({ scale: 1, x: 0, y: 0 });
  const slideZoomRef = useRef(slideZoom);
  slideZoomRef.current = slideZoom;
  /** Пальцы на слайде: по двум считаем щипок, по одному — перетаскивание. */
  const touchesRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchRef = useRef<
    { dist: number; mx: number; my: number; scale: number; x: number; y: number } | null
  >(null);
  const slidePanRef = useRef<{ sx: number; sy: number; x: number; y: number } | null>(null);
  const narrow = useNarrowScreen();
  const [boardDrawMode, setBoardDrawMode] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const obj = scene.findObject(objectId);
  const pdf = obj && isPdfDocumentObject(obj) ? obj : null;
  const sessionPeerId = scene.getSessionPeerId();
  const isBroadcast = pdf ? isPdfBroadcastActive(pdf) : false;
  const isLeader = pdf ? canControlPdfDocument(pdf, sessionPeerId, canEdit) : false;
  /**
   * Кто ведёт — строго тот, кто начал трансляцию.
   *
   * Листать страницы может любой редактор доски (иначе после переподключения
   * ведущий терял бы управление — у него меняется номер участника), но
   * НАДПИСЬ по этому признаку врала: у всех редакторов стояло «вы ведёте».
   * А пока номер участника ещё не получен, она перемигивалась с «только
   * просмотр». Поэтому для надписи — отдельная, строгая проверка.
   */
  const leaderPeerId =
    pdf && typeof pdf.presentationLeaderId === "string" ? pdf.presentationLeaderId : "";
  const isSelfLeader = Boolean(leaderPeerId && sessionPeerId && leaderPeerId === sessionPeerId);
  const liveBadgeText = !sessionPeerId
    ? "Трансляция"
    : isSelfLeader
      ? "Трансляция · вы ведёте"
      : "Трансляция · только просмотр";
  /** Свернутая панель: доска сверху кликабельна, можно рисовать. */
  const docked = Boolean(canEdit && boardDrawMode);

  // Трансляция начинается на весь экран — и у ведущего, и у зрителей.
  // Раньше у ведущего панель сразу сворачивалась вниз: нажимаешь «Трансляция
  // для всех», а презентация ужимается в половину экрана. Свернуть её вниз,
  // чтобы рисовать на доске, по-прежнему можно кнопкой «Доска».

  const closeLocal = useCallback(() => onCloseLocal(), [onCloseLocal]);

  const closeOrStop = useCallback(() => {
    if (isBroadcast && isLeader) {
      void scene.stopPdfBroadcast(objectId).then(() => closeLocal());
      return;
    }
    if (!isBroadcast) closeLocal();
  }, [closeLocal, isBroadcast, isLeader, objectId, scene]);

  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (!pdf) return;
      const pageCount = pdf.pageCount;
      const currentPage = typeof pdf.currentPage === "number" ? pdf.currentPage : 0;

      if (ev.key === "Escape") {
        if (isBroadcast && !isLeader) {
          ev.preventDefault();
          return;
        }
        ev.preventDefault();
        closeOrStop();
        return;
      }

      if (!isLeader) return;

      if (ev.key === "ArrowLeft" || ev.key === "PageUp") {
        ev.preventDefault();
        if (currentPage > 0) void scene.setPdfPage(objectId, currentPage - 1);
      } else if (ev.key === "ArrowRight" || ev.key === "PageDown") {
        ev.preventDefault();
        if (currentPage < pageCount - 1) void scene.setPdfPage(objectId, currentPage + 1);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeOrStop, isBroadcast, isLeader, objectId, pdf, scene]);

  const currentPage = pdf && typeof pdf.currentPage === "number" ? pdf.currentPage : 0;
  const pageCount = pdf ? pdf.pageCount : 0;

  // Новый слайд показываем целиком: приближение прошлого к нему не относится.
  useEffect(() => {
    setSlideZoom({ scale: 1, x: 0, y: 0 });
    touchesRef.current.clear();
    pinchRef.current = null;
    slidePanRef.current = null;
  }, [currentPage, objectId]);

  /** Не даём утащить слайд за пределы его же рамки. */
  function clampSlideZoom(next: { scale: number; x: number; y: number }) {
    const el = bodyRef.current;
    const scale = Math.min(MAX_SLIDE_ZOOM, Math.max(1, next.scale));
    if (scale <= 1 || !el) return { scale, x: 0, y: 0 };
    const limitX = ((scale - 1) * el.clientWidth) / 2;
    const limitY = ((scale - 1) * el.clientHeight) / 2;
    return {
      scale,
      x: Math.min(limitX, Math.max(-limitX, next.x)),
      y: Math.min(limitY, Math.max(-limitY, next.y)),
    };
  }

  function slideCenter() {
    const el = bodyRef.current;
    if (!el) return { cx: 0, cy: 0 };
    const r = el.getBoundingClientRect();
    return { cx: r.x + r.width / 2, cy: r.y + r.height / 2 };
  }

  function onSlidePointerDown(ev: React.PointerEvent<HTMLDivElement>) {
    if (ev.pointerType === "mouse") return;
    const touches = touchesRef.current;
    touches.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    // Захват пальца не обязателен: если браузер его не даёт, щипок всё равно
    // должен работать, поэтому отказ просто пропускаем.
    try {
      ev.currentTarget.setPointerCapture?.(ev.pointerId);
    } catch {
      /* палец уже отпущен или не захватывается */
    }
    if (touches.size === 2) {
      const [a, b] = Array.from(touches.values());
      const { cx, cy } = slideCenter();
      const z = slideZoomRef.current;
      pinchRef.current = {
        dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
        mx: (a.x + b.x) / 2 - cx,
        my: (a.y + b.y) / 2 - cy,
        scale: z.scale,
        x: z.x,
        y: z.y,
      };
      slidePanRef.current = null;
    } else if (touches.size === 1 && slideZoomRef.current.scale > 1) {
      const z = slideZoomRef.current;
      slidePanRef.current = { sx: ev.clientX, sy: ev.clientY, x: z.x, y: z.y };
    }
  }

  function onSlidePointerMove(ev: React.PointerEvent<HTMLDivElement>) {
    const touches = touchesRef.current;
    if (!touches.has(ev.pointerId)) return;
    touches.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

    if (touches.size >= 2 && pinchRef.current) {
      const [a, b] = Array.from(touches.values());
      const start = pinchRef.current;
      const dist = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));
      const { cx, cy } = slideCenter();
      const mx = (a.x + b.x) / 2 - cx;
      const my = (a.y + b.y) / 2 - cy;
      const scale = Math.min(MAX_SLIDE_ZOOM, Math.max(1, (start.scale * dist) / start.dist));
      // Точка между пальцами остаётся на том же месте слайда.
      const k = scale / start.scale;
      setSlideZoom(
        clampSlideZoom({
          scale,
          x: mx - k * (start.mx - start.x),
          y: my - k * (start.my - start.y),
        })
      );
      return;
    }

    if (touches.size === 1 && slidePanRef.current && slideZoomRef.current.scale > 1) {
      const pan = slidePanRef.current;
      setSlideZoom(
        clampSlideZoom({
          scale: slideZoomRef.current.scale,
          x: pan.x + (ev.clientX - pan.sx),
          y: pan.y + (ev.clientY - pan.sy),
        })
      );
    }
  }

  function onSlidePointerUp(ev: React.PointerEvent<HTMLDivElement>) {
    const touches = touchesRef.current;
    touches.delete(ev.pointerId);
    if (touches.size < 2) pinchRef.current = null;
    if (touches.size === 0) slidePanRef.current = null;
    // Чуть отпустил ниже единицы — возвращаем слайд целиком.
    if (slideZoomRef.current.scale <= 1.02 && slideZoomRef.current.scale !== 1) {
      setSlideZoom({ scale: 1, x: 0, y: 0 });
    }
  }

  const slideZoomed = slideZoom.scale > 1.01;

  useLayoutEffect(() => {
    const root = thumbsRef.current;
    if (!root) return;
    root.scrollTop = thumbsScrollRef.current.top;
    root.scrollLeft = thumbsScrollRef.current.left;
  }, [currentPage, pageCount]);

  /**
   * Картинки в ленте держим только у видимых слайдов, и не исходники, а
   * уменьшенные копии: исходник у миниатюры тот же, что у большого слайда —
   * сторона 2560 px, ~15 МБ в распакованном виде.
   */
  useEffect(() => {
    const root = thumbsRef.current;
    if (!root || pageCount <= 1) {
      stripRef.current?.destroy();
      stripRef.current = null;
      return;
    }
    const strip = createLazyThumbStrip(root, ".wb-presentation-overlay__thumb");
    stripRef.current = strip;
    return () => {
      strip.destroy();
      if (stripRef.current === strip) stripRef.current = null;
    };
  }, [objectId, pageCount]);

  useEffect(() => {
    stripRef.current?.refresh();
  }, [currentPage, narrow, docked]);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });
    return () => cancelAnimationFrame(id);
  }, [docked]);

  useEffect(() => {
    const thumbs = thumbsRef.current;
    const body = bodyRef.current;
    if (!thumbs || !pdf || pdf.pageCount <= 1) return;

    const onThumbsWheel = (ev: WheelEvent) => {
      if (ev.ctrlKey || ev.metaKey) return;
      ev.stopPropagation();
    };

    const forwardWheelToBoard = (ev: WheelEvent) => {
      if (ev.ctrlKey || ev.metaKey) return;
      if (!docked) return;
      const vp = viewportRef?.current;
      if (!vp) return;
      ev.preventDefault();
      ev.stopPropagation();
      vp.dispatchEvent(
        new WheelEvent("wheel", {
          bubbles: true,
          cancelable: true,
          deltaX: ev.deltaX,
          deltaY: ev.deltaY,
          deltaMode: ev.deltaMode,
          clientX: ev.clientX,
          clientY: ev.clientY,
          ctrlKey: ev.ctrlKey,
          metaKey: ev.metaKey,
        })
      );
    };

    thumbs.addEventListener("wheel", onThumbsWheel, { passive: true });
    body?.addEventListener("wheel", forwardWheelToBoard, { passive: false });
    return () => {
      thumbs.removeEventListener("wheel", onThumbsWheel);
      body?.removeEventListener("wheel", forwardWheelToBoard);
    };
  }, [pdf, docked, viewportRef]);

  if (!pdf) return null;

  const assetId = pdfCurrentPageAssetId(pdf);
  const title = pdf.title ? String(pdf.title) : "PDF";
  const pageAssetIds = Array.isArray(pdf.pageAssetIds) ? pdf.pageAssetIds : [];

  function setPage(next: number) {
    if (!isLeader) return;
    void scene.setPdfPage(objectId, next);
  }

  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadPresentationPdf(scene, objectId);
      onDownloaded?.();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : "Не удалось скачать презентацию");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div
      className={`wb-presentation-overlay${docked ? " is-board-draw" : ""}`}
      role="dialog"
      aria-label={`Презентация: ${title}`}
    >
      {!docked ? (
        <div
          className="wb-presentation-overlay__backdrop"
          onClick={isBroadcast && !isLeader ? undefined : closeOrStop}
          aria-hidden
        />
      ) : null}
      <div className="wb-presentation-overlay__panel" ref={panelRef}>
        <header className="wb-presentation-overlay__header">
          <div className="wb-presentation-overlay__title-wrap">
            <h2 className="wb-presentation-overlay__title">{title}</h2>
            {isBroadcast ? (
              <span className="wb-presentation-overlay__live-badge">{liveBadgeText}</span>
            ) : null}
          </div>
          <div className="wb-presentation-overlay__header-actions">
            <button
              type="button"
              className="wb-btn wb-btn--ghost wb-btn--sm"
              disabled={downloading}
              onClick={() => void handleDownload()}
              title="Скачать PDF"
            >
              {downloading ? "…" : "Скачать"}
            </button>
            {canEdit ? (
              <button
                type="button"
                className={`wb-btn wb-btn--sm${docked ? " wb-btn--primary" : " wb-btn--ghost"}`}
                onClick={() => setBoardDrawMode((v) => !v)}
                title={
                  docked
                    ? "Развернуть презентацию на весь экран"
                    : "Свернуть вниз — рисовать и двигать объекты на доске"
                }
              >
                {docked ? "На весь экран" : "Доска"}
              </button>
            ) : null}
            {!isBroadcast && canEdit ? (
              <button
                type="button"
                className="wb-btn wb-btn--primary wb-btn--sm"
                onClick={() => void scene.startPdfBroadcast(objectId)}
              >
                Трансляция для всех
              </button>
            ) : null}
            {isBroadcast && isLeader ? (
              <button
                type="button"
                className="wb-btn wb-btn--ghost wb-btn--sm"
                onClick={() => void scene.stopPdfBroadcast(objectId)}
              >
                Завершить трансляцию
              </button>
            ) : null}
            {!isBroadcast || isLeader ? (
              <button type="button" className="wb-btn wb-btn--ghost wb-btn--sm" onClick={closeOrStop}>
                Закрыть (Esc)
              </button>
            ) : null}
          </div>
        </header>
        <div className="wb-presentation-overlay__stage" ref={stageRef}>
          {pageCount > 1 ? (
            <aside
              className="wb-presentation-overlay__thumbs"
              ref={thumbsRef}
              aria-label="Слайды"
              onScroll={(ev) => {
                const el = ev.currentTarget;
                thumbsScrollRef.current = { top: el.scrollTop, left: el.scrollLeft };
              }}
            >
              {pageAssetIds.map((id, i) => (
                <button
                  key={id}
                  type="button"
                  className={`wb-presentation-overlay__thumb${
                    i === currentPage ? " is-active" : ""
                  }`}
                  disabled={!isLeader}
                  title={`Слайд ${i + 1}`}
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => setPage(i)}
                  data-thumb-src={scene.assetContentUrl(id)}
                >
                  <span className="wb-presentation-overlay__thumb-num">{i + 1}</span>
                </button>
              ))}
            </aside>
          ) : null}
          <div
            className={`wb-presentation-overlay__body${
              slideZoomed ? " is-zoomed" : ""
            }`}
            ref={bodyRef}
            onPointerDown={onSlidePointerDown}
            onPointerMove={onSlidePointerMove}
            onPointerUp={onSlidePointerUp}
            onPointerCancel={onSlidePointerUp}
            onDoubleClick={() => setSlideZoom({ scale: 1, x: 0, y: 0 })}
          >
            {assetId ? (
              <img
                className="wb-presentation-overlay__page"
                src={scene.assetContentUrl(assetId)}
                alt={`Страница ${currentPage + 1}`}
                draggable={false}
                style={
                  slideZoomed
                    ? {
                        transform: `translate(${slideZoom.x}px, ${slideZoom.y}px) scale(${slideZoom.scale})`,
                      }
                    : undefined
                }
                onPointerDown={(ev) => {
                  if (!isLeader || pageCount <= 1) return;
                  // Приближенный слайд пальцем двигают, а не листают.
                  if (slideZoomRef.current.scale > 1.01) return;
                  if (touchesRef.current.size > 1) return;
                  if (ev.button !== 0) return;
                  ev.preventDefault();
                  const startX = ev.clientX;
                  const startY = ev.clientY;
                  const pointerId = ev.pointerId;
                  const startPage = currentPage;
                  function onMove(me: PointerEvent) {
                    if (me.pointerId !== pointerId) return;
                  }
                  function onUp(ue: PointerEvent) {
                    if (ue.pointerId !== pointerId) return;
                    window.removeEventListener("pointermove", onMove);
                    window.removeEventListener("pointerup", onUp);
                    window.removeEventListener("pointercancel", onUp);
                    const dx = ue.clientX - startX;
                    const dy = ue.clientY - startY;
                    const dist = Math.hypot(dx, dy);
                    // Короткий клик по слайду — следующий (как в обычных презентациях).
                    if (dist < 14) {
                      if (startPage < pageCount - 1) setPage(startPage + 1);
                      return;
                    }
                    if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
                    if (dx < 0 && startPage < pageCount - 1) setPage(startPage + 1);
                    else if (dx > 0 && startPage > 0) setPage(startPage - 1);
                  }
                  window.addEventListener("pointermove", onMove);
                  window.addEventListener("pointerup", onUp);
                  window.addEventListener("pointercancel", onUp);
                }}
                onClick={(ev) => {
                  // Дубль с pointerup не нужен — только не даём всплыть.
                  ev.stopPropagation();
                }}
              />
            ) : null}
          </div>
        </div>
        <footer className="wb-presentation-overlay__footer">
          <button
            type="button"
            className="wb-btn wb-btn--ghost"
            disabled={!isLeader || currentPage <= 0}
            onPointerDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.stopPropagation();
              setPage(currentPage - 1);
            }}
          >
            ‹ Назад
          </button>
          <span className="wb-presentation-overlay__pager">
            {currentPage + 1} / {pageCount}
          </span>
          <button
            type="button"
            className="wb-btn wb-btn--ghost"
            disabled={!isLeader || currentPage >= pageCount - 1}
            onPointerDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => {
              ev.stopPropagation();
              setPage(currentPage + 1);
            }}
          >
            Вперёд ›
          </button>
        </footer>
      </div>
    </div>
  );
}
